/**
 * Pure helpers for deriving parent-dashboard activity summaries from the children list.
 *
 * Extracted so the page can consume them WITHOUT importing from the
 * RecentActivitySummary component module (which some tests vi.mock, and which would
 * otherwise make these helpers undefined under that mock). RecentActivitySummary keeps
 * its own copies for its story tests; this module is the page's dependency.
 *
 * TO-LL-044: reads exact camelCase keys (lastSessionDate, displayName).
 */

import type { ChildSummary } from '@/types/coins';

/**
 * Format an ISO date string into a localised short date label.
 * Returns null for null/undefined/invalid dates so callers can omit the label.
 */
export function formatActivityDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Count children whose lastSessionDate falls within the last 7 rolling days.
 * Children with null/undefined/invalid lastSessionDate are excluded.
 */
export function computeSessionsThisWeek(children: ChildSummary[]): number {
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  return children.filter((child) => {
    if (!child.lastSessionDate) return false;
    const d = new Date(child.lastSessionDate);
    if (isNaN(d.getTime())) return false;
    return now - d.getTime() <= SEVEN_DAYS_MS;
  }).length;
}

/**
 * Derive the most-recently-active child and their formatted session date.
 * Returns null when no child has any valid session date.
 */
export function deriveLastActive(
  children: ChildSummary[]
): { childName: string; date: string } | null {
  const withSessions = children
    .filter((c) => {
      if (!c.lastSessionDate) return false;
      const d = new Date(c.lastSessionDate);
      return !isNaN(d.getTime());
    })
    .map((c) => ({
      displayName: c.displayName,
      timestamp: new Date(c.lastSessionDate as string).getTime(),
      lastSessionDate: c.lastSessionDate as string,
    }));

  if (withSessions.length === 0) return null;

  const mostRecent = withSessions.reduce((prev, curr) =>
    curr.timestamp > prev.timestamp ? curr : prev
  );

  const date = formatActivityDate(mostRecent.lastSessionDate);
  if (!date) return null;

  return { childName: mostRecent.displayName, date };
}
